import {
  GridRowId,
  GridRowSelectionModel,
  GridToolbarContainer,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid-pro'
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  ToggleButton,
  Tooltip,
} from '@mui/material'
import {
  Addchart,
  AddCircle,
  ChangeCircle,
  CheckCircle,
  Clear,
  CloudDownload,
  Download,
  HeartBroken,
} from '@mui/icons-material'
import React, { useMemo, useState } from 'react'
import ChangePhaseModal from './ChangePhaseModal'
import { useMutation, UseQueryResult } from 'react-query'
import { AxiosResponse } from 'axios'
import SpreadsheetService from '../../../APIs/Python/APIs/SpreadsheetService'
import { useNavigate, useParams } from 'react-router-dom'
import { useStudyContext } from '../../../Contexts/Modules/StudyModule/SelectedStudyModule'
import { listItemsFromDataObjectArr } from '../../../Functions/ListItemGenerator'
import StudyService from '../../../APIs/Python/APIs/StudyService'
import { useMessagePopupContext } from '../../../garvan-react/Components/ModalFeedback/MessagePopup'
import { useGlobalContext } from '../../../Contexts/GlobalTriggers'
import { useUserContext } from '../../../Contexts/UserConfig'
import ExportFormatField from '../StudySelect/ExportFomatField'

interface AnimalToolbarProps {
  selectionModel: GridRowSelectionModel
  filterPhase: string | undefined
  setFilterPhase: any
  inStudy: boolean
  setInStudy: any
  setLoadAll: any
  setShowType: any
  selectedPhases: (string | null)[]
  containerQueries: UseQueryResult[]
  includeCulled: boolean
  setIncludeCulled: any
}
function AnimalToolbar({
  selectionModel,
  filterPhase,
  setFilterPhase,
  inStudy,
  selectedPhases,
  setShowType,
  setInStudy,
  setLoadAll,
  setIncludeCulled,
  includeCulled,
  containerQueries,
}: AnimalToolbarProps) {
  const { whoAmI } = useUserContext()
  const { studyQuery } = useStudyContext()
  const { data: study } = studyQuery
  const { setError } = useMessagePopupContext()
  const { setLoadingDialog } = useGlobalContext()

  const { study_id } = useParams()

  const [openPhase, setOpenPhase] = useState(false)

  const [anchorEl, setAnchorEl] = useState<any>(null)

  const navigate = useNavigate()

  //filtering queries to find only selected
  //checking UAI intersection between selection and queries
  const selectedQueries = useMemo(
    () =>
      containerQueries.filter(
        (q: any) =>
          q?.data
            ?.map((d: { UAI: any }) => d.UAI)
            .filter((UAI: GridRowId) => selectionModel.includes(UAI)).length >
          0,
      ),
    [containerQueries, selectionModel],
  )

  const { mutate: enrollAnimals } = useMutation<any, AxiosResponse>(
    () =>
      StudyService.enrollStudyAnimals(
        whoAmI,
        study_id!,
        selectionModel as number[],
      ),
    {
      onError: (err) => {
        setError(err)
        setLoadingDialog(false)
      },
      onSuccess: () => {
        selectedQueries.forEach((result: any) => result.refetch())
        setLoadingDialog(false)
      },
    },
  )

  const { mutate: startSpreadsheet } = useMutation<any, AxiosResponse>(
    () =>
      SpreadsheetService.startSpreadsheet(
        whoAmI,
        study_id!,
        selectedPhases[0]!,
        selectionModel as number[],
      ),
    {
      onSuccess: (data) => {
        navigate(`../sheet?id=${data.data_collection_id}`)
      },
      onError: (err) => setError(err),
    },
  )

  function phaseMessage(disabled: boolean) {
    if (disabled) {
      return 'Update Phase'
    } else if (selectedPhases.filter((p: any) => p).length === 0) {
      return 'To change phase, animals must first be enrolled'
    } else {
      return 'Phase will be updated only for enrolled animals '
    }
  }

  return (
    <GridToolbarContainer sx={{ display: 'flex', p: 1 }}>
      <ChangePhaseModal
        open={openPhase}
        setOpen={setOpenPhase}
        selections={selectionModel}
        selectedQueries={selectedQueries}
      />
      <Box sx={{ display: 'flex', py: 1 / 2, flex: 1 }}>
        <Box sx={{ minWidth: 100, mr: 1 / 2 }}>
          <GridToolbarDensitySelector />
        </Box>
        <FormControl
          fullWidth
          size={'small'}
          sx={{ mx: 1 / 2, maxWidth: 250, ml: 'auto' }}
        >
          <InputLabel>Filter Phase</InputLabel>
          <Select
            label="Filter Phase"
            IconComponent={() => null}
            endAdornment={
              <IconButton
                size={'small'}
                onClick={() => setFilterPhase(undefined)}
              >
                <Clear />
              </IconButton>
            }
            value={filterPhase || ''}
            onChange={(e) => setFilterPhase(e.target.value)}
          >
            {study?.phases ?
              listItemsFromDataObjectArr(
                study.phases,
                'PhaseID',
                'PhaseID',
                'PhaseName',
                'PhaseSeq',
                false,
              )
            : []}
          </Select>
        </FormControl>
        <Tooltip title={'Only show animals in study'}>
          <ToggleButton
            size={'small'}
            sx={{ mx: 1 / 2 }}
            color={'primary'}
            value={'inStudy'}
            selected={inStudy}
            onChange={() => setInStudy((s: any) => !s)}
          >
            <CheckCircle sx={{ mr: 1 / 2 }} /> Study
          </ToggleButton>
        </Tooltip>
        <Tooltip title={'Include Culled Animals'}>
          <ToggleButton
            size={'small'}
            sx={{ mx: 1 / 2 }}
            color={'primary'}
            value={'includeCulled'}
            selected={includeCulled}
            onChange={() => setIncludeCulled((s: any) => !s)}
          >
            <HeartBroken sx={{ mr: 1 / 2 }} /> Culled
          </ToggleButton>
        </Tooltip>
        <ButtonGroup
          variant={'outlined'}
          color="primary"
          sx={{ mx: 1 / 2 }}
          size={'small'}
        >
          <Tooltip title={`Load ${includeCulled ? 'all' : 'alive &'} enrolled`}>
            <Button
              startIcon={<CloudDownload />}
              onClick={() => {
                setLoadAll(true)
                setShowType('enrolled')
              }}
              disabled={!study || study?.StudyID === 1}
            >
              {'Load'}
            </Button>
          </Tooltip>
          <Tooltip title={`Enrol Animals`}>
            <Button
              startIcon={<AddCircle />}
              onClick={() => enrollAnimals()}
              disabled={
                selectionModel.length < 1 || !selectedPhases.includes(null)
              }
            >
              {'Enroll'}
            </Button>
          </Tooltip>
          <Tooltip title={phaseMessage(selectionModel.length === 0)}>
            <Button
              startIcon={<ChangeCircle />}
              onClick={() => setOpenPhase(true)}
              disabled={selectionModel.length === 0}
            >
              Update
            </Button>
          </Tooltip>
          <Tooltip
            title={
              (
                selectedPhases.filter((p: any) => p).length > 0 &&
                selectedPhases.length < 2
              ) ?
                'Begin data entry in a new spreadsheet'
              : 'Selected Animals must all be in the same phase'
            }
          >
            <Button
              startIcon={<Addchart />}
              disabled={
                !(
                  selectedPhases.filter((p: any) => p).length > 0 &&
                  selectedPhases.length < 2
                ) || selectionModel.length < 1
              }
              onClick={() => startSpreadsheet()}
              color={'success'}
            >
              Data
            </Button>
          </Tooltip>
          <Tooltip title={'Export Animals'}>
            <Button
              color="info"
              startIcon={<Download />}
              onClick={(e) => {
                setAnchorEl(e.currentTarget)
              }}
              disabled={selectionModel.length < 1}
            >
              Export
            </Button>
          </Tooltip>
          <ExportFormatField
            selectedId={study_id!}
            rowId={study_id!}
            animals={selectionModel as number[]}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            source={'Animal'}
          />
        </ButtonGroup>
      </Box>
    </GridToolbarContainer>
  )
}

export default AnimalToolbar
